import { ColDef, ColumnResizedEvent, ColumnState, ValueFormatterParams, ColumnVisibleEvent, IComponent } from 'ag-grid-community';
import { GroupCellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/groupCellRenderer';

export const DEFAULT_COL_DEF: ColDef = {
  menuTabs: ['columnsMenuTab', 'generalMenuTab'],
  columnsMenuParams: {
    suppressColumnFilter: true,
    suppressColumnSelectAll: true,
    suppressColumnExpandAll: true
  },
  resizable: true
};

interface ColumnSizes {
  [colId: string]: number; // actualWidth
}

const saveToSessionStorage = <T>(page: string, key: string, values: T) => sessionStorage.setItem(`${page}_${key}`, JSON.stringify(values));
const saveToLocalStorage = <T>(page: string, key: string, values: T) => localStorage.setItem(`${page}_${key}`, JSON.stringify(values));
const getFromSessionStorage = <T>(page: string, key: string): T | null => {
  const value = sessionStorage.getItem(`${page}_${key}`);

  return value ? JSON.parse(value) : null;
};
const getFromLocalStorage = <T>(page: string, key: string): T | null => {
  const value = localStorage.getItem(`${page}_${key}`);

  return value ? JSON.parse(value) : null;
};

const deleteFromSessionStorage = (page: string, key: string) => sessionStorage.removeItem(`${page}_${key}`);
export const saveVisibleColumns = (page: string, columns: string[]) => saveToSessionStorage(page, 'columns', columns);
export const getVisibleColumns = (page: string) => getFromSessionStorage<string[]>(page, 'columns');
export const getVisibleColumnsFromEvent = (e: ColumnVisibleEvent): string[] =>
  e.columnApi
    .getColumnState()
    .filter((c) => c.hide === false)
    .map((c) => c.colId!);
export const onColumnVisible = (page: string) => (e: ColumnVisibleEvent) => {
  saveVisibleColumns(page, getVisibleColumnsFromEvent(e));
};
export const saveColumnsSize = (page: string, sizes: ColumnSizes) => saveToSessionStorage(page, 'columnsSizes', sizes);
export const saveColumnState = (page: string, sortOptions: ColumnState[]) => saveToLocalStorage(page, 'columnState', sortOptions);
export const clearColumnsSize = (page: string) => deleteFromSessionStorage(page, 'columnsSizes');
export const getColumnsSize = (page: string) => getFromSessionStorage<ColumnSizes>(page, 'columnsSizes');
export const getColumnState = (page: string) => getFromLocalStorage<ColumnState[]>(page, 'columnState');
export const onColumnsResized = (page: string) => (e: ColumnResizedEvent) => {
  if (e.source === 'api') return;
  const stored = getColumnsSize(page);
  if (e.source === 'sizeColumnsToFit' && stored) return;
  const sizes: ColumnSizes = e.columns?.reduce((acc, col) => ({ ...acc, [col.getColId()]: col.getActualWidth() }), {}) || {};
  saveColumnsSize(page, { ...stored, ...sizes });
};
export const addHeaderTooltip = (cols: ColDef[]): ColDef[] => cols.map((c) => ({ ...c, headerTooltip: c.headerName }));
const isValueNullOrZero = (value: string | number): boolean => value === '' || value === '0' || value === 0 || value === null;

const comparator =
  ({ valueFormatter }: ColDef) =>
  (valueA: string | number | any, valueB: string | number | any, nodeA: any, nodeB: any, isInverted: any): number => {
    let shouldSortDownZeros = false;

    if (Array.isArray(valueA)) {
      const trades = nodeA.data.positions.length > nodeB.data.positions.length;
      return trades ? 1 : -1;
    }

    if (typeof valueA === 'object' && typeof valueB === 'object') {
      if (!valueA?.firstName || !valueB?.firstName || !valueA?.lastName || !valueB?.lastName) {
        return 0;
      }
      const result = valueA?.firstName.localeCompare(valueB?.firstName);

      return result !== 0 ? result : valueA?.lastName.localeCompare(valueB?.lastName);
    }

    if (valueFormatter && typeof valueFormatter === 'function') {
      const formatterA = valueFormatter({ value: valueA } as ValueFormatterParams);
      const formatterB = valueFormatter({ value: valueB } as ValueFormatterParams);

      if (formatterA === '' || formatterB === '') shouldSortDownZeros = true;
    }

    const parsedA = Number(valueA);
    const parsedB = Number(valueB);

    if (shouldSortDownZeros && isValueNullOrZero(valueA)) {
      return isInverted ? -1 : 1;
    }

    if (shouldSortDownZeros && isValueNullOrZero(valueB)) {
      return isInverted ? 1 : -1;
    }

    if (!Number.isNaN(parsedA) && !Number.isNaN(parsedB)) {
      return parsedA - parsedB;
    }

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB);
    }

    return 0;
  };

export const addSortable = (cols: ColDef[]): ColDef[] => cols.map((c) => ({ ...c, comparator: comparator(c), sortable: c.sortable }));
export const setInitialHideColumns = (cols: ColDef[], visibleColumns: string[] | null): ColDef[] =>
  visibleColumns === null ? cols : cols.map((col) => ({ ...col, initialHide: visibleColumns.indexOf(col.field!) === -1 }));

export class GroupCellRenderer implements IComponent<GroupCellRendererParams> {
  host: HTMLElement;
  field: string;
  constructor() {
    this.host = document.createElement('span');
    this.field = 'ccy';
  }
  init(params: GroupCellRendererParams) {
    const value = params.data?.[this.field] ? params.data[this.field] : params.value;
    this.host.setAttribute('title', value);
    this.host.innerHTML = value;
  }
  getGui() {
    return this.host;
  }
}

export class GroupCellRendererExtended extends GroupCellRenderer {
  field: string;
  constructor() {
    super();
    this.field = 'origin';
  }
}
